import React, { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Field from "@/components/FormInput/Field";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { debounce } from "@mui/material/utils";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { loadPlacesLibrary } from "@/lib/googleMaps/loader";
import type {
  AutocompleteSessionToken,
  FormattableText,
  PlacePrediction,
} from "@/lib/googleMaps/types";
import { toLocationMap } from "./toLocationMap";
import type { LocationMap, PlacePickerMode } from "./types";

const DEBOUNCE_MS = 400;

/**
 * Places API (New) dropped legacy's "(cities)" type collection — there are no
 * collections at all — so the city filter has to name the underlying primary
 * types. `includedPrimaryTypes` accepts at most five.
 */
const CITY_PRIMARY_TYPES = ["locality", "administrative_area_level_3"];

interface PlaceOption {
  locationMap: LocationMap;
  /**
   * Absent for the option synthesised from `value`: a persisted location has no
   * prediction behind it, so it renders without match highlighting.
   */
  prediction?: PlacePrediction;
}

export interface PlacePickerProps {
  value: LocationMap | null;
  onChange: (value: LocationMap | null) => void;
  mode?: PlacePickerMode;
  /**
   * "labelled" renders MUI's built-in floating label. "bare" renders none, for
   * forms that already print their own label above the field — two labels
   * otherwise collide over the notched outline.
   */
  appearance?: "labelled" | "bare";
  label?: string;
  /**
   * Applied to the input so an external `<label htmlFor>` binds to it. Without
   * this, MUI generates its own id and any external label is inert.
   */
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
}

function sameLocation(a: LocationMap, b: LocationMap): boolean {
  // Legacy rows can carry an empty place_id, so fall back to the description.
  if (a.place_id && b.place_id) {
    return a.place_id === b.place_id;
  }

  return a.description === b.description;
}

/**
 * Bolds the substrings Google reports as matching the query. StringRange
 * offsets are [startOffset, endOffset), so each range is a direct slice — no
 * need for the autosuggest-highlight dependency the old component used.
 */
function renderHighlighted(
  formattable: FormattableText | undefined,
  fallback: string
): React.ReactNode {
  const text = formattable?.text ?? fallback;
  const matches = formattable?.matches ?? [];

  if (!matches.length) {
    return text;
  }

  // `start` doubles as a stable key: offsets within one string are unique.
  const parts: Array<{ text: string; highlight: boolean; start: number }> = [];
  let cursor = 0;

  matches.forEach(({ startOffset, endOffset }) => {
    if (startOffset > cursor) {
      parts.push({
        text: text.slice(cursor, startOffset),
        highlight: false,
        start: cursor,
      });
    }

    parts.push({
      text: text.slice(startOffset, endOffset),
      highlight: true,
      start: startOffset,
    });
    cursor = endOffset;
  });

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), highlight: false, start: cursor });
  }

  return parts.map((part) => (
    <Box
      key={part.start}
      component="span"
      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
    >
      {part.text}
    </Box>
  ));
}

const PlacePicker = ({
  value,
  onChange,
  mode = "city",
  appearance = "labelled",
  label,
  id,
  placeholder,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: PlacePickerProps) => {
  const theme = useTheme();
  const { t } = useTranslation("translation.forms");

  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<PlaceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const sessionTokenRef = useRef<AutocompleteSessionToken | null>(null);
  // Guards against an earlier, slower response overwriting a later one.
  const requestIdRef = useRef(0);

  const fetchSuggestions = useMemo(
    () =>
      debounce(
        async (input: string, requestMode: PlacePickerMode, requestId: number) => {
          try {
            const places = await loadPlacesLibrary();

            if (!sessionTokenRef.current) {
              sessionTokenRef.current = new places.AutocompleteSessionToken();
            }

            const { suggestions } =
              await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input,
                sessionToken: sessionTokenRef.current,
                ...(requestMode === "city"
                  ? { includedPrimaryTypes: CITY_PRIMARY_TYPES }
                  : {}),
              });

            if (requestId !== requestIdRef.current) {
              return;
            }

            setOptions(
              suggestions
                .map((suggestion) => suggestion.placePrediction)
                .filter((prediction): prediction is PlacePrediction =>
                  Boolean(prediction)
                )
                .map((prediction) => ({
                  locationMap: toLocationMap(prediction),
                  prediction,
                }))
            );
            setLoading(false);
          } catch (fetchError) {
            if (requestId !== requestIdRef.current) {
              return;
            }

            console.error("Place autocomplete request failed", fetchError);
            setOptions([]);
            setFailed(true);
            setLoading(false);
          }
        },
        DEBOUNCE_MS
      ),
    []
  );

  useEffect(() => () => fetchSuggestions.clear(), [fetchSuggestions]);

  useEffect(() => {
    if (!query) {
      // Bump the id so an in-flight response for a now-cleared box is dropped.
      requestIdRef.current += 1;
      fetchSuggestions.clear();
      setOptions([]);
      setLoading(false);
      setFailed(false);
      return;
    }

    requestIdRef.current += 1;
    setLoading(true);
    setFailed(false);
    fetchSuggestions(query, mode, requestIdRef.current);
  }, [query, mode, fetchSuggestions]);

  const valueOption = useMemo<PlaceOption | null>(
    () => (value ? { locationMap: value } : null),
    [value]
  );

  // MUI warns when the controlled value is not among the options, so the
  // persisted location is prepended until a matching suggestion arrives.
  const mergedOptions = useMemo(() => {
    if (!valueOption) {
      return options;
    }

    const alreadyPresent = options.some((option) =>
      sameLocation(option.locationMap, valueOption.locationMap)
    );

    return alreadyPresent ? options : [valueOption, ...options];
  }, [options, valueOption]);

  const resolvedHelperText = error
    ? helperText
    : failed
      ? t("LocationSearchUnavailable")
      : helperText;

  return (
    <>
      <Autocomplete<PlaceOption, false, false, false>
        id={id}
        // There is nothing to reveal until the user types, so an open/close
        // chevron would promise a list that does not exist. The react-select
        // implementation this replaces also stripped its dropdown indicator.
        forcePopupIcon={false}
        sx={[
          {
            width: "100%",
            "& .MuiOutlinedInput-root": {
              background: theme?.palette?.common?.white,
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        options={mergedOptions}
        value={valueOption}
        disabled={disabled}
        loading={loading}
        // Google has already ranked and filtered; re-filtering locally would
        // hide valid suggestions whose text does not literally contain the query.
        filterOptions={(unfiltered) => unfiltered}
        getOptionLabel={(option) => option.locationMap.description}
        isOptionEqualToValue={(option, selected) =>
          sameLocation(option.locationMap, selected.locationMap)
        }
        noOptionsText={t("NoLocations")}
        autoComplete
        includeInputInList
        onChange={(_event, selected) => {
          // A selection ends the billing session; the next search mints a token.
          sessionTokenRef.current = null;
          setQuery("");
          setOptions(selected ? [selected] : []);
          onChange(selected ? selected.locationMap : null);
        }}
        onInputChange={(_event, nextInput, reason) => {
          // Only user typing drives a fetch. "reset" fires when MUI echoes the
          // selected option back into the box, and treating that as a query is
          // what made the old component re-search its own selection.
          if (reason === "input") {
            setQuery(nextInput);
          } else if (reason === "clear") {
            setQuery("");
          }
        }}
        renderInput={(params) => (
          <Field
            {...params}
            label={appearance === "bare" ? undefined : label ?? t("AddLocation")}
            placeholder={placeholder}
            required={required}
            error={error || failed}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps.htmlInput,
                // With no visible label of its own, the field relies on an
                // external <label htmlFor={id}>. Name it directly when the
                // caller supplied no id, so it is never left unnamed.
                ...(appearance === "bare" && !id
                  ? { "aria-label": label ?? t("AddLocation") }
                  : {}),
              },
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <PlaceOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.slotProps.input?.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={({ key, ...optionProps }, option) => (
          <li {...optionProps} key={option.locationMap.place_id || key}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                {renderHighlighted(
                  option.prediction?.mainText,
                  option.locationMap.description
                )}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {option.prediction?.secondaryText?.text ?? ""}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )}
      />
      {resolvedHelperText ? (
        <FormHelperText error={error || failed}>{resolvedHelperText}</FormHelperText>
      ) : null}
    </>
  );
};

export default PlacePicker;
